import type { ContentHash } from "@cogo/publisher-core";

import {
  hashCanonicalContent,
  stableCanonicalStringify,
} from "./canonical-content-hash";
import { assertPlainJsonContract } from "./application-ontology-generation-binding";

export const UI_BLOCK_RUNTIME_EXECUTION_REQUEST_VERSION =
  "cogo.ui-block-runtime-execution-request.v1" as const;
export const UI_BLOCK_RUNTIME_EXECUTION_RECEIPT_VERSION =
  "cogo.ui-block-runtime-execution-receipt.v1" as const;
export const UI_BLOCK_EVIDENCE_RECEIPT_VERSION =
  "cogo.ui-block-evidence-receipt.v1" as const;
export const UI_BLOCK_CONSUMER_RECEIPT_VERSION =
  "cogo.ui-block-consumer-receipt.v1" as const;
export const UI_BLOCK_PRODUCTION_READBACK_RECEIPT_VERSION =
  "cogo.ui-block-production-readback-receipt.v1" as const;
export const UI_BLOCK_E2E_CLOSURE_RECEIPT_VERSION =
  "cogo.ui-block-e2e-closure-receipt.v1" as const;

export type UiBlockEvidenceStatus =
  | "pass"
  | "fail"
  | "blocked"
  | "not-run"
  | "fallback";
export type UiBlockEvidenceLayer =
  | "live-qwen-profile"
  | "replay-journey"
  | "six-document"
  | "schema"
  | "validator"
  | "browser"
  | "design-swap"
  | "datasource";
export type UiBlockConsumerKind =
  | "studio-core"
  | "bot"
  | "react-native-expo"
  | "flutter";

export interface UiBlockAuthorityHashesV1 {
  spec: ContentHash;
  library: ContentHash;
  design: ContentHash;
}

export interface UiBlockDocumentHashesV1 {
  block: ContentHash;
  ui: ContentHash;
  events: ContentHash;
  flow: ContentHash;
  variables: ContentHash;
  datasource: ContentHash;
}

export interface UiBlockSourceRevisionsV1 {
  cogoWeb: string;
  cogoPlatform: string;
}

export interface UiBlockClosureIdentityV1 {
  sourceRevisions: UiBlockSourceRevisionsV1;
  applicationHash: ContentHash;
  blockHash: ContentHash;
  contextHash: ContentHash;
  authorityHashes: UiBlockAuthorityHashesV1;
  documentHashes: UiBlockDocumentHashesV1;
}

export interface UiBlockRuntimeExecutionPolicyV1 {
  timeoutMs: number;
  maxSteps: number;
  maxTraceBytes: number;
  network: "disabled";
  allowedHostEffects: string[];
}

export interface UiBlockRuntimeExecutionRequestBodyV1
  extends UiBlockClosureIdentityV1 {
  executionId: string;
  flow: Record<string, unknown>;
  variables: Record<string, unknown>;
  datasource: Record<string, unknown>;
  initialState: Record<string, unknown>;
  mocks: Record<string, unknown>;
  policy: UiBlockRuntimeExecutionPolicyV1;
}

export interface UiBlockRuntimeExecutionRequestV1
  extends UiBlockRuntimeExecutionRequestBodyV1 {
  schemaVersion: typeof UI_BLOCK_RUNTIME_EXECUTION_REQUEST_VERSION;
  requestHash: ContentHash;
}

export interface UiBlockRuntimeTraceStepV1 {
  stepId: string;
  action: string;
  status: "ok" | "error" | "skipped";
  effect: string | null;
}

export interface UiBlockRuntimeExecutionRunV1 {
  runId: string;
  flowId: string;
  status: "pass" | "fail";
  startedStateHash: ContentHash;
  finalStateHash: ContentHash;
  steps: UiBlockRuntimeTraceStepV1[];
}

export interface UiBlockRuntimeExecutionReceiptBodyV1
  extends UiBlockClosureIdentityV1 {
  executionId: string;
  requestHash: ContentHash;
  runtimeSourceHash: ContentHash;
  engine: "@cogo/runtime-v2#executeFlowWithTrace";
  mockAdapter: "MockRestAdapter";
  status: "pass" | "fail" | "blocked";
  runs: UiBlockRuntimeExecutionRunV1[];
  blockers: string[];
}

export interface UiBlockRuntimeExecutionReceiptV1
  extends UiBlockRuntimeExecutionReceiptBodyV1 {
  schemaVersion: typeof UI_BLOCK_RUNTIME_EXECUTION_RECEIPT_VERSION;
  receiptHash: ContentHash;
}

export interface UiBlockEvidenceReceiptBodyV1
  extends UiBlockClosureIdentityV1 {
  layer: UiBlockEvidenceLayer;
  subjectId: string;
  status: UiBlockEvidenceStatus;
  fallbackUsed: boolean;
  evidenceRefs: string[];
  blockers: string[];
}

export interface UiBlockEvidenceReceiptV1
  extends UiBlockEvidenceReceiptBodyV1 {
  schemaVersion: typeof UI_BLOCK_EVIDENCE_RECEIPT_VERSION;
  receiptHash: ContentHash;
}

export interface UiBlockConsumerObservationV1 {
  packageLoaded: boolean;
  renderedOrLowered: boolean;
  designReleaseResolved: boolean;
  actionFlowExecuted: boolean;
  stateTransitionObserved: boolean;
  datasourceResolved: boolean;
}

export interface UiBlockConsumerReceiptBodyV1
  extends UiBlockClosureIdentityV1 {
  consumer: UiBlockConsumerKind;
  designReleaseHash: ContentHash;
  status: "pass" | "fail" | "blocked";
  observations: UiBlockConsumerObservationV1;
  evidenceRefs: string[];
  blockers: string[];
}

export interface UiBlockConsumerReceiptV1
  extends UiBlockConsumerReceiptBodyV1 {
  schemaVersion: typeof UI_BLOCK_CONSUMER_RECEIPT_VERSION;
  receiptHash: ContentHash;
}

export interface UiBlockProductionReadbackReceiptBodyV1
  extends UiBlockClosureIdentityV1 {
  cogoWebRevision: string;
  cogoPlatformRevision: string;
  runtimeWorkerVersion: string;
  platformWorkerVersion: string;
  responseHash: ContentHash;
  runtimeSourceHash: ContentHash;
  status: "pass" | "fail" | "blocked";
  evidenceRefs: string[];
  blockers: string[];
}

export interface UiBlockProductionReadbackReceiptV1
  extends UiBlockProductionReadbackReceiptBodyV1 {
  schemaVersion: typeof UI_BLOCK_PRODUCTION_READBACK_RECEIPT_VERSION;
  receiptHash: ContentHash;
}

export interface UiBlockE2EClosureEvidenceV1
  extends UiBlockClosureIdentityV1 {
  liveProfiles: UiBlockEvidenceReceiptV1[];
  replayJourneys: UiBlockEvidenceReceiptV1[];
  sixDocument: UiBlockEvidenceReceiptV1;
  schemaValidation: UiBlockEvidenceReceiptV1;
  validatorValidation: UiBlockEvidenceReceiptV1;
  runtime: UiBlockRuntimeExecutionReceiptV1;
  browser: UiBlockEvidenceReceiptV1;
  designSwap: UiBlockEvidenceReceiptV1;
  datasource: UiBlockEvidenceReceiptV1;
  consumers: UiBlockConsumerReceiptV1[];
  productionReadback: UiBlockProductionReadbackReceiptV1 | null;
}

export interface UiBlockE2EClosureSummaryV1 {
  liveProfilesPassed: number;
  liveProfilesRequired: 18;
  replayJourneysPassed: number;
  replayJourneysRequired: 54;
  consumersPassed: number;
  consumersRequired: 4;
  runtimeRunCount: number;
}

export interface UiBlockE2EClosureReceiptBodyV1
  extends UiBlockClosureIdentityV1 {
  evaluatedAt: string;
  evidenceHash: ContentHash;
  summary: UiBlockE2EClosureSummaryV1;
  allowed: boolean;
  blockers: string[];
}

export interface UiBlockE2EClosureReceiptV1
  extends UiBlockE2EClosureReceiptBodyV1 {
  schemaVersion: typeof UI_BLOCK_E2E_CLOSURE_RECEIPT_VERSION;
  receiptHash: ContentHash;
}

const HASH = /^sha256:[a-f0-9]{64}$/u;

function exactKeys(
  value: unknown,
  keys: readonly string[],
  label: string,
): asserts value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`UI_BLOCK_CLOSURE_INVALID_${label}`);
  }
  const actual = Object.keys(value);
  if (
    actual.length !== keys.length ||
    actual.some((key) => !keys.includes(key)) ||
    keys.some((key) => !Object.prototype.hasOwnProperty.call(value, key))
  ) {
    throw new Error(`UI_BLOCK_CLOSURE_INVALID_${label}_KEYS`);
  }
}

function text(value: unknown, label: string): asserts value is string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`UI_BLOCK_CLOSURE_INVALID_${label}`);
  }
}

function hash(value: unknown, label: string): asserts value is ContentHash {
  if (typeof value !== "string" || !HASH.test(value)) {
    throw new Error(`UI_BLOCK_CLOSURE_INVALID_${label}`);
  }
}

function textArray(value: unknown, label: string): asserts value is string[] {
  if (!Array.isArray(value)) throw new Error(`UI_BLOCK_CLOSURE_INVALID_${label}`);
  const seen = new Set<string>();
  for (const item of value) {
    text(item, label);
    if (seen.has(item)) throw new Error(`UI_BLOCK_CLOSURE_DUPLICATE_${label}`);
    seen.add(item);
  }
}

function jsonRecord(
  value: unknown,
  label: string,
): asserts value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`UI_BLOCK_CLOSURE_INVALID_${label}`);
  }
}

const IDENTITY_KEYS = [
  "sourceRevisions",
  "applicationHash",
  "blockHash",
  "contextHash",
  "authorityHashes",
  "documentHashes",
] as const;

function assertAuthorityHashes(
  value: unknown,
): asserts value is UiBlockAuthorityHashesV1 {
  exactKeys(value, ["spec", "library", "design"], "AUTHORITY_HASHES");
  hash(value.spec, "SPEC_HASH");
  hash(value.library, "LIBRARY_HASH");
  hash(value.design, "DESIGN_HASH");
}

function assertDocumentHashes(
  value: unknown,
): asserts value is UiBlockDocumentHashesV1 {
  exactKeys(
    value,
    ["block", "ui", "events", "flow", "variables", "datasource"],
    "DOCUMENT_HASHES",
  );
  for (const key of [
    "block",
    "ui",
    "events",
    "flow",
    "variables",
    "datasource",
  ] as const) {
    hash(value[key], `DOCUMENT_${key.toUpperCase()}_HASH`);
  }
}

function assertSourceRevisions(
  value: unknown,
): asserts value is UiBlockSourceRevisionsV1 {
  exactKeys(value, ["cogoWeb", "cogoPlatform"], "SOURCE_REVISIONS");
  text(value.cogoWeb, "COGO_WEB_REVISION");
  text(value.cogoPlatform, "COGO_PLATFORM_REVISION");
}

function assertIdentity(value: Record<string, unknown>): void {
  assertSourceRevisions(value.sourceRevisions);
  hash(value.applicationHash, "APPLICATION_HASH");
  hash(value.blockHash, "BLOCK_HASH");
  hash(value.contextHash, "CONTEXT_HASH");
  assertAuthorityHashes(value.authorityHashes);
  assertDocumentHashes(value.documentHashes);
}

function canonicalBody<T>(value: T): T {
  return JSON.parse(stableCanonicalStringify(value)) as T;
}

function assertHashMatches(
  body: unknown,
  actual: unknown,
  label: string,
): void {
  hash(actual, label);
  if (hashCanonicalContent(body) !== actual) {
    throw new Error(`UI_BLOCK_CLOSURE_${label}_MISMATCH`);
  }
}

export function sameUiBlockClosureIdentity(
  expected: UiBlockClosureIdentityV1,
  actual: UiBlockClosureIdentityV1,
): boolean {
  return stableCanonicalStringify(expected) === stableCanonicalStringify(actual);
}

export function uiBlockClosureIdentityOf(
  value: UiBlockClosureIdentityV1,
): UiBlockClosureIdentityV1 {
  return canonicalBody({
    sourceRevisions: value.sourceRevisions,
    applicationHash: value.applicationHash,
    blockHash: value.blockHash,
    contextHash: value.contextHash,
    authorityHashes: value.authorityHashes,
    documentHashes: value.documentHashes,
  });
}

export function deriveSingleBlockUiBlockClosureIdentityV1(input: {
  sourceRevisions: UiBlockSourceRevisionsV1;
  authorityHashes: UiBlockAuthorityHashesV1;
  documents: {
    block: object;
    ui: object;
    events: object;
    flow: object;
    variables: object;
    datasource: object;
  };
}): UiBlockClosureIdentityV1 {
  const documents = canonicalBody(input.documents);
  const documentHashes = canonicalBody({
    block: hashCanonicalContent(documents.block),
    ui: hashCanonicalContent(documents.ui),
    events: hashCanonicalContent(documents.events),
    flow: hashCanonicalContent(documents.flow),
    variables: hashCanonicalContent(documents.variables),
    datasource: hashCanonicalContent(documents.datasource),
  });
  const blockHash = hashCanonicalContent(documents);
  return uiBlockClosureIdentityOf({
    sourceRevisions: canonicalBody(input.sourceRevisions),
    authorityHashes: canonicalBody(input.authorityHashes),
    documentHashes,
    blockHash,
    applicationHash: hashCanonicalContent({
      blocks: [blockHash],
      documents: documentHashes,
    }),
    contextHash: hashCanonicalContent({
      authorityHashes: input.authorityHashes,
      blockHash,
    }),
  });
}

export function createUiBlockRuntimeExecutionRequestV1(
  body: UiBlockRuntimeExecutionRequestBodyV1,
): UiBlockRuntimeExecutionRequestV1 {
  const canonical = canonicalBody(body);
  const request: UiBlockRuntimeExecutionRequestV1 = {
    schemaVersion: UI_BLOCK_RUNTIME_EXECUTION_REQUEST_VERSION,
    ...canonical,
    requestHash: hashCanonicalContent(canonical),
  };
  assertUiBlockRuntimeExecutionRequestV1(request);
  return request;
}

export function assertUiBlockRuntimeExecutionRequestV1(
  value: unknown,
): asserts value is UiBlockRuntimeExecutionRequestV1 {
  assertPlainJsonContract(value, "ui-block-runtime-execution-request");
  exactKeys(
    value,
    [
      "schemaVersion",
      ...IDENTITY_KEYS,
      "executionId",
      "flow",
      "variables",
      "datasource",
      "initialState",
      "mocks",
      "policy",
      "requestHash",
    ],
    "RUNTIME_REQUEST",
  );
  if (value.schemaVersion !== UI_BLOCK_RUNTIME_EXECUTION_REQUEST_VERSION) {
    throw new Error("UI_BLOCK_CLOSURE_RUNTIME_REQUEST_VERSION");
  }
  assertIdentity(value);
  text(value.executionId, "EXECUTION_ID");
  for (const key of [
    "flow",
    "variables",
    "datasource",
    "initialState",
    "mocks",
  ] as const) {
    jsonRecord(value[key], `RUNTIME_REQUEST_${key.toUpperCase()}`);
  }
  exactKeys(
    value.policy,
    ["timeoutMs", "maxSteps", "maxTraceBytes", "network", "allowedHostEffects"],
    "RUNTIME_POLICY",
  );
  for (const key of ["timeoutMs", "maxSteps", "maxTraceBytes"] as const) {
    if (!Number.isInteger(value.policy[key]) || (value.policy[key] as number) < 1) {
      throw new Error(`UI_BLOCK_CLOSURE_INVALID_POLICY_${key.toUpperCase()}`);
    }
  }
  if (value.policy.network !== "disabled") {
    throw new Error("UI_BLOCK_CLOSURE_NETWORK_POLICY_FORBIDDEN");
  }
  textArray(value.policy.allowedHostEffects, "ALLOWED_HOST_EFFECTS");
  const body = Object.fromEntries(
    Object.entries(value).filter(
      ([key]) => key !== "schemaVersion" && key !== "requestHash",
    ),
  );
  assertHashMatches(body, value.requestHash, "REQUEST_HASH");
}

function assertRuntimeRun(
  value: unknown,
): asserts value is UiBlockRuntimeExecutionRunV1 {
  exactKeys(
    value,
    ["runId", "flowId", "status", "startedStateHash", "finalStateHash", "steps"],
    "RUNTIME_RUN",
  );
  text(value.runId, "RUN_ID");
  text(value.flowId, "FLOW_ID");
  if (!['pass', 'fail'].includes(String(value.status))) {
    throw new Error("UI_BLOCK_CLOSURE_INVALID_RUN_STATUS");
  }
  hash(value.startedStateHash, "STARTED_STATE_HASH");
  hash(value.finalStateHash, "FINAL_STATE_HASH");
  if (!Array.isArray(value.steps)) throw new Error("UI_BLOCK_CLOSURE_INVALID_RUN_STEPS");
  for (const step of value.steps) {
    exactKeys(step, ["stepId", "action", "status", "effect"], "RUNTIME_STEP");
    text(step.stepId, "STEP_ID");
    text(step.action, "STEP_ACTION");
    if (!["ok", "error", "skipped"].includes(String(step.status))) {
      throw new Error("UI_BLOCK_CLOSURE_INVALID_STEP_STATUS");
    }
    if (step.effect !== null) text(step.effect, "STEP_EFFECT");
  }
}

export function createUiBlockRuntimeExecutionReceiptV1(
  body: UiBlockRuntimeExecutionReceiptBodyV1,
): UiBlockRuntimeExecutionReceiptV1 {
  const canonical = canonicalBody(body);
  const receipt: UiBlockRuntimeExecutionReceiptV1 = {
    schemaVersion: UI_BLOCK_RUNTIME_EXECUTION_RECEIPT_VERSION,
    ...canonical,
    receiptHash: hashCanonicalContent(canonical),
  };
  assertUiBlockRuntimeExecutionReceiptV1(receipt);
  return receipt;
}

export function assertUiBlockRuntimeExecutionReceiptV1(
  value: unknown,
): asserts value is UiBlockRuntimeExecutionReceiptV1 {
  assertPlainJsonContract(value, "ui-block-runtime-execution-receipt");
  exactKeys(
    value,
    [
      "schemaVersion",
      ...IDENTITY_KEYS,
      "executionId",
      "requestHash",
      "runtimeSourceHash",
      "engine",
      "mockAdapter",
      "status",
      "runs",
      "blockers",
      "receiptHash",
    ],
    "RUNTIME_RECEIPT",
  );
  if (value.schemaVersion !== UI_BLOCK_RUNTIME_EXECUTION_RECEIPT_VERSION) {
    throw new Error("UI_BLOCK_CLOSURE_RUNTIME_RECEIPT_VERSION");
  }
  assertIdentity(value);
  text(value.executionId, "EXECUTION_ID");
  hash(value.requestHash, "REQUEST_HASH");
  hash(value.runtimeSourceHash, "RUNTIME_SOURCE_HASH");
  if (value.engine !== "@cogo/runtime-v2#executeFlowWithTrace") {
    throw new Error("UI_BLOCK_CLOSURE_RUNTIME_ENGINE_INVALID");
  }
  if (value.mockAdapter !== "MockRestAdapter") {
    throw new Error("UI_BLOCK_CLOSURE_MOCK_ADAPTER_INVALID");
  }
  if (!["pass", "fail", "blocked"].includes(String(value.status))) {
    throw new Error("UI_BLOCK_CLOSURE_RUNTIME_STATUS_INVALID");
  }
  if (!Array.isArray(value.runs)) throw new Error("UI_BLOCK_CLOSURE_RUNTIME_RUNS_INVALID");
  value.runs.forEach(assertRuntimeRun);
  const runs = value.runs as UiBlockRuntimeExecutionRunV1[];
  textArray(value.blockers, "RUNTIME_BLOCKERS");
  if (
    value.status === "pass" &&
    (runs.length === 0 ||
      runs.some((run) => run.status !== "pass") ||
      runs.some((run) => run.steps.length === 0) ||
      runs.some((run) => run.steps.some((step) => step.status === "error")) ||
      value.blockers.length > 0)
  ) {
    throw new Error("UI_BLOCK_CLOSURE_RUNTIME_PASS_INVALID");
  }
  const body = Object.fromEntries(
    Object.entries(value).filter(
      ([key]) => key !== "schemaVersion" && key !== "receiptHash",
    ),
  );
  assertHashMatches(body, value.receiptHash, "RECEIPT_HASH");
}

export function createUiBlockEvidenceReceiptV1(
  body: UiBlockEvidenceReceiptBodyV1,
): UiBlockEvidenceReceiptV1 {
  const canonical = canonicalBody(body);
  const receipt: UiBlockEvidenceReceiptV1 = {
    schemaVersion: UI_BLOCK_EVIDENCE_RECEIPT_VERSION,
    ...canonical,
    receiptHash: hashCanonicalContent(canonical),
  };
  assertUiBlockEvidenceReceiptV1(receipt);
  return receipt;
}

export function assertUiBlockEvidenceReceiptV1(
  value: unknown,
): asserts value is UiBlockEvidenceReceiptV1 {
  assertPlainJsonContract(value, "ui-block-evidence-receipt");
  exactKeys(
    value,
    [
      "schemaVersion",
      ...IDENTITY_KEYS,
      "layer",
      "subjectId",
      "status",
      "fallbackUsed",
      "evidenceRefs",
      "blockers",
      "receiptHash",
    ],
    "EVIDENCE_RECEIPT",
  );
  if (value.schemaVersion !== UI_BLOCK_EVIDENCE_RECEIPT_VERSION) {
    throw new Error("UI_BLOCK_CLOSURE_EVIDENCE_RECEIPT_VERSION");
  }
  assertIdentity(value);
  if (![
    "live-qwen-profile",
    "replay-journey",
    "six-document",
    "schema",
    "validator",
    "browser",
    "design-swap",
    "datasource",
  ].includes(String(value.layer))) {
    throw new Error("UI_BLOCK_CLOSURE_EVIDENCE_LAYER_INVALID");
  }
  text(value.subjectId, "EVIDENCE_SUBJECT_ID");
  if (!["pass", "fail", "blocked", "not-run", "fallback"].includes(String(value.status))) {
    throw new Error("UI_BLOCK_CLOSURE_EVIDENCE_STATUS_INVALID");
  }
  if (typeof value.fallbackUsed !== "boolean") {
    throw new Error("UI_BLOCK_CLOSURE_EVIDENCE_FALLBACK_INVALID");
  }
  textArray(value.evidenceRefs, "EVIDENCE_REFS");
  textArray(value.blockers, "EVIDENCE_BLOCKERS");
  if (
    value.status === "pass" &&
    (value.fallbackUsed || value.evidenceRefs.length === 0 || value.blockers.length > 0)
  ) {
    throw new Error("UI_BLOCK_CLOSURE_EVIDENCE_PASS_INVALID");
  }
  const body = Object.fromEntries(
    Object.entries(value).filter(
      ([key]) => key !== "schemaVersion" && key !== "receiptHash",
    ),
  );
  assertHashMatches(body, value.receiptHash, "RECEIPT_HASH");
}

function assertConsumerObservations(
  value: unknown,
): asserts value is UiBlockConsumerObservationV1 {
  exactKeys(
    value,
    [
      "packageLoaded",
      "renderedOrLowered",
      "designReleaseResolved",
      "actionFlowExecuted",
      "stateTransitionObserved",
      "datasourceResolved",
    ],
    "CONSUMER_OBSERVATIONS",
  );
  for (const key of Object.keys(value)) {
    if (typeof value[key] !== "boolean") {
      throw new Error(`UI_BLOCK_CLOSURE_INVALID_CONSUMER_${key.toUpperCase()}`);
    }
  }
}

export function createUiBlockConsumerReceiptV1(
  body: UiBlockConsumerReceiptBodyV1,
): UiBlockConsumerReceiptV1 {
  const canonical = canonicalBody(body);
  const receipt: UiBlockConsumerReceiptV1 = {
    schemaVersion: UI_BLOCK_CONSUMER_RECEIPT_VERSION,
    ...canonical,
    receiptHash: hashCanonicalContent(canonical),
  };
  assertUiBlockConsumerReceiptV1(receipt);
  return receipt;
}

export function assertUiBlockConsumerReceiptV1(
  value: unknown,
): asserts value is UiBlockConsumerReceiptV1 {
  assertPlainJsonContract(value, "ui-block-consumer-receipt");
  exactKeys(
    value,
    [
      "schemaVersion",
      ...IDENTITY_KEYS,
      "consumer",
      "designReleaseHash",
      "status",
      "observations",
      "evidenceRefs",
      "blockers",
      "receiptHash",
    ],
    "CONSUMER_RECEIPT",
  );
  if (value.schemaVersion !== UI_BLOCK_CONSUMER_RECEIPT_VERSION) {
    throw new Error("UI_BLOCK_CLOSURE_CONSUMER_RECEIPT_VERSION");
  }
  assertIdentity(value);
  if (!["studio-core", "bot", "react-native-expo", "flutter"].includes(String(value.consumer))) {
    throw new Error("UI_BLOCK_CLOSURE_CONSUMER_INVALID");
  }
  hash(value.designReleaseHash, "CONSUMER_DESIGN_RELEASE_HASH");
  if (!["pass", "fail", "blocked"].includes(String(value.status))) {
    throw new Error("UI_BLOCK_CLOSURE_CONSUMER_STATUS_INVALID");
  }
  assertConsumerObservations(value.observations);
  textArray(value.evidenceRefs, "CONSUMER_EVIDENCE_REFS");
  textArray(value.blockers, "CONSUMER_BLOCKERS");
  if (
    value.status === "pass" &&
    (Object.values(value.observations).some((observed) => observed !== true) ||
      value.evidenceRefs.length === 0 ||
      value.blockers.length > 0)
  ) {
    throw new Error("UI_BLOCK_CLOSURE_CONSUMER_PASS_INVALID");
  }
  const body = Object.fromEntries(
    Object.entries(value).filter(
      ([key]) => key !== "schemaVersion" && key !== "receiptHash",
    ),
  );
  assertHashMatches(body, value.receiptHash, "RECEIPT_HASH");
}

export function createUiBlockProductionReadbackReceiptV1(
  body: UiBlockProductionReadbackReceiptBodyV1,
): UiBlockProductionReadbackReceiptV1 {
  const canonical = canonicalBody(body);
  const receipt: UiBlockProductionReadbackReceiptV1 = {
    schemaVersion: UI_BLOCK_PRODUCTION_READBACK_RECEIPT_VERSION,
    ...canonical,
    receiptHash: hashCanonicalContent(canonical),
  };
  assertUiBlockProductionReadbackReceiptV1(receipt);
  return receipt;
}

export function assertUiBlockProductionReadbackReceiptV1(
  value: unknown,
): asserts value is UiBlockProductionReadbackReceiptV1 {
  assertPlainJsonContract(value, "ui-block-production-readback-receipt");
  exactKeys(
    value,
    [
      "schemaVersion",
      ...IDENTITY_KEYS,
      "cogoWebRevision",
      "cogoPlatformRevision",
      "runtimeWorkerVersion",
      "platformWorkerVersion",
      "responseHash",
      "runtimeSourceHash",
      "status",
      "evidenceRefs",
      "blockers",
      "receiptHash",
    ],
    "PRODUCTION_READBACK_RECEIPT",
  );
  if (value.schemaVersion !== UI_BLOCK_PRODUCTION_READBACK_RECEIPT_VERSION) {
    throw new Error("UI_BLOCK_CLOSURE_PRODUCTION_READBACK_VERSION");
  }
  assertIdentity(value);
  for (const key of [
    "cogoWebRevision",
    "cogoPlatformRevision",
    "runtimeWorkerVersion",
    "platformWorkerVersion",
  ] as const) text(value[key], `PRODUCTION_${key.toUpperCase()}`);
  hash(value.responseHash, "PRODUCTION_RESPONSE_HASH");
  hash(value.runtimeSourceHash, "PRODUCTION_RUNTIME_SOURCE_HASH");
  if (!["pass", "fail", "blocked"].includes(String(value.status))) {
    throw new Error("UI_BLOCK_CLOSURE_PRODUCTION_STATUS_INVALID");
  }
  textArray(value.evidenceRefs, "PRODUCTION_EVIDENCE_REFS");
  textArray(value.blockers, "PRODUCTION_BLOCKERS");
  if (
    value.status === "pass" &&
    (value.evidenceRefs.length === 0 || value.blockers.length > 0)
  ) {
    throw new Error("UI_BLOCK_CLOSURE_PRODUCTION_PASS_INVALID");
  }
  const body = Object.fromEntries(
    Object.entries(value).filter(
      ([key]) => key !== "schemaVersion" && key !== "receiptHash",
    ),
  );
  assertHashMatches(body, value.receiptHash, "RECEIPT_HASH");
}

export function createUiBlockE2EClosureReceiptV1(
  body: UiBlockE2EClosureReceiptBodyV1,
): UiBlockE2EClosureReceiptV1 {
  const canonical = canonicalBody(body);
  const receipt: UiBlockE2EClosureReceiptV1 = {
    schemaVersion: UI_BLOCK_E2E_CLOSURE_RECEIPT_VERSION,
    ...canonical,
    receiptHash: hashCanonicalContent(canonical),
  };
  assertUiBlockE2EClosureReceiptV1(receipt);
  return receipt;
}

export function assertUiBlockE2EClosureReceiptV1(
  value: unknown,
): asserts value is UiBlockE2EClosureReceiptV1 {
  assertPlainJsonContract(value, "ui-block-e2e-closure-receipt");
  exactKeys(
    value,
    [
      "schemaVersion",
      ...IDENTITY_KEYS,
      "evaluatedAt",
      "evidenceHash",
      "summary",
      "allowed",
      "blockers",
      "receiptHash",
    ],
    "E2E_CLOSURE_RECEIPT",
  );
  if (value.schemaVersion !== UI_BLOCK_E2E_CLOSURE_RECEIPT_VERSION) {
    throw new Error("UI_BLOCK_CLOSURE_RECEIPT_VERSION");
  }
  assertIdentity(value);
  text(value.evaluatedAt, "EVALUATED_AT");
  hash(value.evidenceHash, "EVIDENCE_HASH");
  exactKeys(
    value.summary,
    [
      "liveProfilesPassed",
      "liveProfilesRequired",
      "replayJourneysPassed",
      "replayJourneysRequired",
      "consumersPassed",
      "consumersRequired",
      "runtimeRunCount",
    ],
    "CLOSURE_SUMMARY",
  );
  const summary = value.summary as Record<
    | "liveProfilesPassed" | "liveProfilesRequired"
    | "replayJourneysPassed" | "replayJourneysRequired"
    | "consumersPassed" | "consumersRequired" | "runtimeRunCount",
    number
  >;
  for (const key of [
    "liveProfilesPassed",
    "replayJourneysPassed",
    "consumersPassed",
    "runtimeRunCount",
  ] as const) {
    if (!Number.isInteger(summary[key]) || summary[key] < 0) {
      throw new Error(`UI_BLOCK_CLOSURE_INVALID_SUMMARY_${key.toUpperCase()}`);
    }
  }
  if (
    summary.liveProfilesRequired !== 18 ||
    summary.replayJourneysRequired !== 54 ||
    summary.consumersRequired !== 4
  ) {
    throw new Error("UI_BLOCK_CLOSURE_REQUIRED_COUNTS_INVALID");
  }
  if (typeof value.allowed !== "boolean") {
    throw new Error("UI_BLOCK_CLOSURE_ALLOWED_INVALID");
  }
  textArray(value.blockers, "CLOSURE_BLOCKERS");
  if (value.allowed && value.blockers.length > 0) {
    throw new Error("UI_BLOCK_CLOSURE_ALLOWED_WITH_BLOCKERS");
  }
  if (
    value.allowed &&
    (summary.liveProfilesPassed !== 18 ||
      summary.replayJourneysPassed !== 54 ||
      summary.consumersPassed !== 4 ||
      summary.runtimeRunCount < 1)
  ) {
    throw new Error("UI_BLOCK_CLOSURE_ALLOWED_WITH_INCOMPLETE_SUMMARY");
  }
  if (!value.allowed && value.blockers.length === 0) {
    throw new Error("UI_BLOCK_CLOSURE_BLOCKED_WITHOUT_REASON");
  }
  const body = Object.fromEntries(
    Object.entries(value).filter(
      ([key]) => key !== "schemaVersion" && key !== "receiptHash",
    ),
  );
  assertHashMatches(body, value.receiptHash, "RECEIPT_HASH");
}
